// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { GoogleMap, Polygon, MarkerF, useLoadScript } from '@react-google-maps/api';
import { Box, Paper, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { styles } from './MapComponent.styles';

const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const libraries: ("places" | "geometry" | "geocoding")[] = ["places", "geometry", "geocoding"];

// Dark map style to match the admin's Grid dark theme (standard Google "dark"
// styled-map JSON — applied via the map `styles` option, no cloud mapId needed).
export const DARK_MAP_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#212121' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#757575' }] },
  { featureType: 'administrative.country', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#181818' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'poi.park', elementType: 'labels.text.stroke', stylers: [{ color: '#1b1b1b' }] },
  { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#2c2c2c' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#8a8a8a' }] },
  { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#373737' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3c3c3c' }] },
  { featureType: 'road.highway.controlled_access', elementType: 'geometry', stylers: [{ color: '#4e4e4e' }] },
  { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'transit', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#000000' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#3d3d3d' }] },
];

export interface MapComponentProps {
  mapCenter: google.maps.LatLngLiteral;
  mapBounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  } | null;
  polygonPath: google.maps.LatLngLiteral[];
  cornerMarkers: {[key: string]: google.maps.LatLngLiteral};
  handleMarkerDrag?: (position: google.maps.LatLngLiteral, markerId: string) => void;
  onMarkerDragStart?: () => void;
  onMarkerDragEnd?: (position: google.maps.LatLngLiteral, markerId: string) => void;
  locationName: string;
  neighborhoodBoundaries: {
    id: string;
    name: string;
    path: google.maps.LatLngLiteral[];
    isActive: boolean;
  }[];
  centerPoint?: google.maps.LatLngLiteral;
  onCenterPointDrag?: (position: google.maps.LatLngLiteral) => void;
  isPlace?: boolean;
  height: number;
  width: number;
  zoom?: number;
  customMarkers?: CustomMarker[];
  isAddingPoints?: boolean;
  onAddPoint?: (position: google.maps.LatLngLiteral) => void;
  onDeletePoint?: (index: number) => void;
  onInsertPoint?: (position: google.maps.LatLngLiteral, index: number) => void;
  lockCameraOnDrag?: boolean; // New prop to control camera locking behavior
  disableBoundsFitting?: boolean; // Completely disable automatic bounds fitting
}

interface CustomMarker {
  id: string;
  position: google.maps.LatLngLiteral;
  icon?: google.maps.Icon | google.maps.Symbol;
  tooltip?: React.ReactNode;
  onClick?: () => void;
  label?: google.maps.MarkerLabel;
}

const MapComponent: React.FC<MapComponentProps> = React.memo(({
  mapCenter,
  mapBounds,
  polygonPath,
  cornerMarkers,
  handleMarkerDrag,
  onMarkerDragStart,
  onMarkerDragEnd,
  locationName,
  neighborhoodBoundaries,
  centerPoint,
  onCenterPointDrag,
  isPlace = false,
  height,
  width,
  zoom: initialZoom = 15,
  customMarkers,
  isAddingPoints = false,
  onAddPoint,
  onDeletePoint,
  onInsertPoint,
  lockCameraOnDrag = true, // Default to true for bounding box editing
  disableBoundsFitting = false
}) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey,
    libraries
  });

  const mapRef = useRef<google.maps.Map | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [selectedMarker, setSelectedMarker] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [hoveredSegment, setHoveredSegment] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [lockedCamera, setLockedCamera] = useState<{center: google.maps.LatLngLiteral, zoom: number} | null>(null);

  // Memoize map container style to prevent recreation
  const mapContainerStyle = useMemo(() => ({ width, height }), [width, height]);

  // Memoize map options to prevent recreation
  const mapOptions = useMemo(() => ({
    styles: DARK_MAP_STYLE,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    clickableIcons: false,
    gestureHandling: isDragging ? 'none' as const : 'cooperative' as const,
    zoomControl: !isDragging,
    scrollwheel: !isAddingPoints && !isDragging,
    disableDoubleClickZoom: isAddingPoints || isDragging,
    // Prevent automatic camera adjustments during marker interactions
    restriction: mapBounds && !isDragging ? {
      latLngBounds: mapBounds,
      strictBounds: false
    } : undefined
  }), [isAddingPoints, mapBounds, isDragging]);

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setMap(map);
  }, []);

  // Handle map click for adding points
  const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (isAddingPoints && onAddPoint && e.latLng) {
      onAddPoint({
        lat: e.latLng.lat(),
        lng: e.latLng.lng()
      });
    }
  }, [isAddingPoints, onAddPoint]);

  // Calculate midpoint between two points - memoized
  const getMidpoint = useCallback((p1: google.maps.LatLngLiteral, p2: google.maps.LatLngLiteral): google.maps.LatLngLiteral => ({
    lat: (p1.lat + p2.lat) / 2,
    lng: (p1.lng + p2.lng) / 2
  }), []);

  // Lock camera position during marker operations
  const lockCamera = useCallback(() => {
    if (!map) return;
    const center = map.getCenter();
    const zoom = map.getZoom();
    if (center && zoom) {
      setLockedCamera({
        center: { lat: center.lat(), lng: center.lng() },
        zoom
      });
    }
  }, [map]);

  // Unlock camera and restore position if needed
  const unlockCamera = useCallback(() => {
    setLockedCamera(null);
  }, []);

  // Debounced bounds update to prevent excessive map operations
  const updateMapBounds = useCallback((map: google.maps.Map, bounds: google.maps.LatLngBounds) => {
    // Skip bounds update if disabled, camera is locked, or during dragging
    if (disableBoundsFitting || isDragging || lockedCamera) return;
    
    // Use requestAnimationFrame to batch DOM updates
    requestAnimationFrame(() => {
      map.fitBounds(bounds);
    });
  }, [disableBoundsFitting, isDragging, lockedCamera]);

  // Handle bounds and center updates with debouncing
  useEffect(() => {
    if (!map) return;

    // NEVER update camera during dragging - this is the main culprit
    if (isDragging) {
      console.log('[MapComponent] SKIPPING camera updates - isDragging:', isDragging);
      return;
    }

    const timeoutId = setTimeout(() => {
      // Don't update camera if it's locked during marker operations
      if (lockedCamera) {
        console.log('[MapComponent] Restoring locked camera position');
        map.setCenter(lockedCamera.center);
        map.setZoom(lockedCamera.zoom);
        return;
      }

      if (mapBounds) {
        console.log('[MapComponent] Fitting to bounds');
        const bounds = new google.maps.LatLngBounds(
          { lat: mapBounds.south, lng: mapBounds.west },
          { lat: mapBounds.north, lng: mapBounds.east }
        );
        updateMapBounds(map, bounds);
      } else if (mapCenter) {
        console.log('[MapComponent] Setting center');
        map.setCenter(mapCenter);
        map.setZoom(initialZoom);
      }
    }, 100); // 100ms debounce

    return () => clearTimeout(timeoutId);
  }, [map, mapBounds, mapCenter, initialZoom, updateMapBounds, isDragging, lockedCamera]);

  // AGGRESSIVE: Override Google Maps internal methods during dragging to prevent ANY camera changes
  useEffect(() => {
    if (!map || !isDragging) return;

    console.log('[MapComponent] LOCKING ALL MAP CAMERA METHODS - isDragging:', isDragging);

    // Store original methods
    const originalFitBounds = map.fitBounds.bind(map);
    const originalPanToBounds = map.panToBounds?.bind(map);
    const originalSetCenter = map.setCenter.bind(map);
    const originalSetZoom = map.setZoom.bind(map);
    const originalPanTo = map.panTo?.bind(map);

    // Override with no-ops during dragging
    map.fitBounds = () => {
      console.log('[MapComponent] BLOCKED fitBounds call during dragging');
    };
    if (map.panToBounds) {
      map.panToBounds = () => {
        console.log('[MapComponent] BLOCKED panToBounds call during dragging');
      };
    }
    map.setCenter = () => {
      console.log('[MapComponent] BLOCKED setCenter call during dragging');
    };
    map.setZoom = () => {
      console.log('[MapComponent] BLOCKED setZoom call during dragging');
    };
    if (map.panTo) {
      map.panTo = () => {
        console.log('[MapComponent] BLOCKED panTo call during dragging');
      };
    }

    return () => {
      console.log('[MapComponent] RESTORING MAP CAMERA METHODS - isDragging:', isDragging);
      // Restore original methods when not dragging
      map.fitBounds = originalFitBounds;
      if (originalPanToBounds) map.panToBounds = originalPanToBounds;
      map.setCenter = originalSetCenter;
      map.setZoom = originalSetZoom;
      if (originalPanTo) map.panTo = originalPanTo;
    };
  }, [map, isDragging]);

  // Memoize polygon options to prevent recreation
  const polygonOptions = useMemo(() => ({
    fillColor: '#4285f4',
    fillOpacity: 0.3,
    strokeColor: '#4285f4',
    strokeOpacity: 1,
    strokeWeight: 2
  }), []);

  // Memoize neighborhood boundary polygons
  const neighborhoodPolygons = useMemo(() => {
    if (isPlace || neighborhoodBoundaries.length === 0) return null;
    
    return neighborhoodBoundaries.map((boundary) => (
      <Polygon
        key={boundary.id}
        paths={boundary.path}
        options={{
          fillColor: boundary.isActive ? '#4285f4' : '#808080',
          fillOpacity: 0.2,
          strokeColor: boundary.isActive ? '#4285f4' : '#808080',
          strokeOpacity: 0.8,
          strokeWeight: 2
        }}
      />
    ));
  }, [isPlace, neighborhoodBoundaries]);

  // Memoize vertex markers to prevent recreation
  const vertexMarkers = useMemo(() => {
    if (isPlace || polygonPath.length === 0) return null;

    return polygonPath.map((point, index) => (
      <React.Fragment key={`point-${index}`}>
        {/* Vertex marker */}
        <MarkerF
          position={point}
          draggable={true}
          icon={{
            path: window.google?.maps.SymbolPath.CIRCLE,
            scale: 6,
            fillColor: selectedMarker === `vertex-${index}` ? '#FF0000' : '#4285f4',
            fillOpacity: 1,
            strokeWeight: 2,
            strokeColor: '#FFFFFF',
          }}
          onMouseOver={() => setSelectedMarker(`vertex-${index}`)}
          onMouseOut={() => setSelectedMarker(null)}
          onDragStart={() => {
            setIsDragging(true);
            if (lockCameraOnDrag) {
              lockCamera();
            }
            if (onMarkerDragStart) {
              onMarkerDragStart();
            }
          }}
          onDragEnd={(e) => {
            setIsDragging(false);
            if (lockCameraOnDrag) {
              unlockCamera();
            }
            if (e.latLng) {
              const position = {
                lat: e.latLng.lat(),
                lng: e.latLng.lng()
              };
              const markerId = `vertex-${index}`;
              
              if (onMarkerDragEnd) {
                onMarkerDragEnd(position, markerId);
              } else if (handleMarkerDrag) {
                handleMarkerDrag(position, markerId);
              }
            }
          }}
        />
        
        {/* Midpoint marker for inserting new points */}
        {index < polygonPath.length - 1 && (
          <MarkerF
            position={getMidpoint(point, polygonPath[index + 1])}
            icon={{
              path: window.google?.maps.SymbolPath.CIRCLE,
              scale: 4,
              fillColor: '#FFFFFF',
              fillOpacity: 0.8,
              strokeWeight: 2,
              strokeColor: '#4285f4',
            }}
            onMouseOver={() => setHoveredSegment(index)}
            onMouseOut={() => setHoveredSegment(null)}
            onClick={() => {
              if (onInsertPoint) {
                const midpoint = getMidpoint(point, polygonPath[index + 1]);
                onInsertPoint(midpoint, index + 1);
              }
            }}
          />
        )}
        
        {/* Delete button for vertex */}
        {selectedMarker === `vertex-${index}` && polygonPath.length > 3 && (
          <Box
            sx={{
              ...styles.deleteButton,
              position: 'absolute',
              zIndex: 1000,
              pointerEvents: 'auto'
            }}
          >
            <IconButton
              size="small"
              onClick={() => onDeletePoint && onDeletePoint(index)}
              sx={styles.deleteButton}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </React.Fragment>
    ));
  }, [isPlace, polygonPath, selectedMarker, handleMarkerDrag, onMarkerDragStart, onMarkerDragEnd, getMidpoint, onInsertPoint, onDeletePoint, lockCamera, unlockCamera, lockCameraOnDrag]);

  // Memoize center point marker
  const centerPointMarker = useMemo(() => {
    if (!centerPoint) return null;

    return (
      <MarkerF
        position={centerPoint}
        draggable={!isPlace && !!onCenterPointDrag}
        icon={isPlace ? {
          path: window.google?.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#FF0000',
          fillOpacity: 1,
          strokeWeight: 2,
          strokeColor: '#FFFFFF',
        } : {
          path: window.google?.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#4285f4',
          fillOpacity: 1,
          strokeWeight: 2,
          strokeColor: '#FFFFFF',
        }}
        onDragStart={() => {
          setIsDragging(true);
          if (lockCameraOnDrag) {
            lockCamera();
          }
        }}
        onDragEnd={(e) => {
          setIsDragging(false);
          if (lockCameraOnDrag) {
            unlockCamera();
          }
          if (e.latLng && onCenterPointDrag) {
            onCenterPointDrag({
              lat: e.latLng.lat(),
              lng: e.latLng.lng()
            });
          }
        }}
      />
    );
  }, [centerPoint, isPlace, onCenterPointDrag, lockCamera, unlockCamera, lockCameraOnDrag]);

  // Memoize custom markers
  const customMarkerElements = useMemo(() => {
    if (!customMarkers || customMarkers.length === 0) return null;

    return customMarkers.map((marker) => (
      <MarkerF
        key={marker.id}
        position={marker.position}
        icon={marker.icon}
        onClick={(e) => {
          e.domEvent.stopPropagation();
          setSelectedMarker(marker.id);
          marker.onClick?.();
        }}
      />
    ));
  }, [customMarkers]);

  if (!isLoaded) return <div>Loading maps...</div>;

  return (
    <Box sx={{ ...styles.container, width, height }}>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={mapCenter}
        zoom={initialZoom}
        onLoad={onLoad}
        onClick={handleMapClick}
        options={mapOptions}
      >
        {/* Display all neighborhood boundaries */}
        {neighborhoodPolygons}

        {/* Active polygon being edited - only for non-places */}
        {!isPlace && polygonPath.length > 0 && (
          <Polygon
            path={polygonPath}
            options={polygonOptions}
          />
        )}
        
        {/* Vertex markers and midpoint markers */}
        {vertexMarkers}

        {/* Place marker or center point */}
        {centerPointMarker}

        {/* Custom markers */}
        {customMarkerElements}
      </GoogleMap>
      
      {customMarkers?.map((marker) => (
        marker.tooltip && selectedMarker === marker.id && (
          <Paper
            key={`tooltip-${marker.id}`}
            sx={styles.tooltip}
          >
            <Box sx={styles.tooltipContent}>
              <Box sx={styles.tooltipText}>
                {marker.tooltip}
              </Box>
              <IconButton 
                size="small" 
                onClick={() => setSelectedMarker(null)}
                sx={styles.closeButton}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        )
      ))}
    </Box>
  );
});

MapComponent.displayName = 'MapComponent';

export default MapComponent; 